import "@/App.css";
// Install the global axios safety net BEFORE any component imports — this
// ensures every API call (even those still using the old hardcoded
// REACT_APP_BACKEND_URL pattern) gets rewritten to a same-origin path when
// the app is served from a production custom domain.
import "./lib/axiosSetup";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./utils/queryClient";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import RequireAdmin from "./components/RequireAdmin";
import ScrollToTop from "./components/ScrollToTop";

// Eagerly loaded: home + auth entry points (landing & first interaction).
import Home from "./pages/Home";
import Login from "./pages/Login";

// Code-split: every heavier page is lazy-loaded so the initial bundle stays small.
const Booking           = lazy(() => import("./pages/Booking"));
const BookingPage       = lazy(() => import("./pages/BookingPage"));
const Pricing           = lazy(() => import("./pages/Pricing"));
const Resources         = lazy(() => import("./pages/Resources"));
const ResourceSuccess   = lazy(() => import("./pages/ResourceSuccess"));
const PremiumSuccess    = lazy(() => import("./pages/PremiumSuccess"));
const About             = lazy(() => import("./pages/About"));
const StudentDashboard  = lazy(() => import("./pages/StudentDashboard"));
const AdminDashboard    = lazy(() => import("./pages/AdminDashboard"));
const TimeslotsManagement = lazy(() => import("./pages/TimeslotsManagement"));
const BookingSuccess    = lazy(() => import("./pages/BookingSuccess"));
const MiniGames         = lazy(() => import("./pages/MiniGames"));
const ChordDictionary   = lazy(() => import("./pages/ChordDictionary"));
const PracticeSession   = lazy(() => import("./pages/PracticeSession"));
const Register          = lazy(() => import("./pages/Register"));
const ForgotPassword    = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword     = lazy(() => import("./pages/ResetPassword"));
const Terms             = lazy(() => import("./pages/Terms"));
const Receipt           = lazy(() => import("./pages/Receipt"));

// Minimal fallback — matches the app's cream background so route transitions
// don't flash white. Kept intentionally lightweight (no spinner) for perceived
// fluidity; chunks load in <300ms on a warm cache.
const RouteFallback = () => (
  <div
    className="min-h-screen bg-[#FAFAF7]"
    aria-busy="true"
    data-testid="route-fallback"
  />
);

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/booking" element={<BookingPage />} />
                <Route path="/booking-old" element={<Booking />} />
                <Route path="/tarifs" element={<Pricing />} />
                <Route path="/ressources" element={<Resources />} />
                <Route path="/ressources/success" element={<ResourceSuccess />} />
                <Route path="/premium/success" element={<PremiumSuccess />} />
                <Route path="/a-propos" element={<About />} />
                <Route path="/dashboard" element={<StudentDashboard />} />
                <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                <Route path="/admin/timeslots" element={<RequireAdmin><TimeslotsManagement /></RequireAdmin>} />
                <Route path="/mini-jeux" element={<MiniGames />} />
                <Route path="/dictionnaire-accords" element={<ChordDictionary />} />
                <Route path="/session-guitare" element={<PracticeSession />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/conditions" element={<Terms />} />
                <Route path="/recu/:id" element={<Receipt />} />
                <Route path="/booking-success" element={<BookingSuccess />} />
              </Routes>
            </Suspense>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
